// In-app AppsFlyer diagnostic panel — accessible via 5-tap on Settings
// version footer. Since MacinCloud can't cable an iPhone to Xcode's
// Console, this panel is our ONLY way to eyeball SDK state on-device:
// plugin availability, DEV_KEY / APP_ID presence, init status, live
// AppsFlyer UID, last-event timestamp, and a "Fire test event" button
// that pushes a real `af_test_event` to AppsFlyer so the wizard's
// Automated Test flow can flip its checklist to green.
//
// Kept intentionally single-file (no Shadcn dependency dance) and
// dark-themed for high contrast on light Settings background.

import { useEffect, useState } from "react";
import { X, Copy, Zap, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  getAppsFlyerDiagnostics,
  getAppsFlyerUID,
  logEvent,
  initAppsFlyer,
} from "@/lib/appsflyer";

function Row({ label, value, mono = false, testid }) {
  const good = value === true || value === "yes";
  const bad = value === false || value === "no" || value === "(empty)" || value === null;
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-white/5">
      <span className="text-xs text-white/60 flex-shrink-0">{label}</span>
      <span
        data-testid={testid}
        className={`text-xs text-right ${mono ? "font-mono" : ""} ${
          good ? "text-emerald-400" : bad ? "text-rose-400" : "text-white"
        }`}
      >
        {value === true ? "✓ yes" : value === false ? "✗ no" : String(value ?? "—")}
      </span>
    </div>
  );
}

export default function AppsFlyerDebugModal({ open, onClose }) {
  const [diag, setDiag] = useState(null);
  const [uid, setUid] = useState(null);
  const [firing, setFiring] = useState(false);

  const refresh = async () => {
    const d = getAppsFlyerDiagnostics();
    setDiag(d);
    if (d.enabled) {
      try { setUid(await getAppsFlyerUID()); } catch { setUid(null); }
    } else {
      setUid(null);
    }
  };

  useEffect(() => {
    if (open) refresh();
  }, [open]);

  if (!open) return null;

  const copyAll = async () => {
    const dump = JSON.stringify({ ...diag, uid }, null, 2);
    try {
      await navigator.clipboard.writeText(dump);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const fireTest = async () => {
    setFiring(true);
    try {
      const res = await logEvent("af_test_event", {
        af_source: "in_app_debug_panel",
        af_ts: new Date().toISOString(),
      });
      if (res?.ok) {
        toast.success("Test event fired ✓ — check AppsFlyer in 1-2 min");
        await refresh();
      } else {
        toast.error(`Fire failed: ${res?.reason || "unknown"}`);
      }
    } catch (e) {
      toast.error(`Error: ${e?.message || e}`);
    } finally {
      setFiring(false);
    }
  };

  const forceInit = async () => {
    try {
      await initAppsFlyer();
      toast.success("initSDK called");
      await refresh();
    } catch (e) {
      toast.error(`init failed: ${e?.message || e}`);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="appsflyer-debug-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        dir="ltr"
        className="w-full max-w-md bg-neutral-900 text-white rounded-3xl p-5 shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm font-black">AppsFlyer Diagnostics</div>
            <div className="text-[10px] text-white/40 mt-0.5">In-app debug · 5-tap on version</div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={refresh}
              className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center active:bg-white/20"
              data-testid="appsflyer-debug-refresh"
              aria-label="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center active:bg-white/20"
              data-testid="appsflyer-debug-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {diag ? (
          <div className="space-y-0">
            <Row label="Native platform" value={diag.platform} testid="afd-platform" />
            <Row label="Is native app" value={diag.isNative} testid="afd-native" />
            <Row label="Plugin available" value={diag.pluginAvailable} testid="afd-plugin" />
            <Row label="SDK enabled" value={diag.enabled} testid="afd-enabled" />
            <Row label="DEV_KEY present" value={diag.devKeyPresent} testid="afd-devkey-present" />
            <Row label="DEV_KEY (masked)" value={diag.devKey} mono testid="afd-devkey" />
            <Row label="APP_ID" value={diag.appId} mono testid="afd-appid" />
            <Row label="Initialized" value={diag.initialized} testid="afd-init" />
            <Row label="AppsFlyer UID" value={uid || "(not fetched yet)"} mono testid="afd-uid" />
            {diag.lastInitError && (
              <div className="mt-2 p-2 rounded-xl bg-rose-500/10 border border-rose-500/30">
                <div className="text-[10px] text-rose-300 font-bold mb-1">Last init error</div>
                <div className="text-[10px] font-mono text-rose-200 break-all">{diag.lastInitError}</div>
              </div>
            )}
            {diag.lastEvent && (
              <div className="mt-2 p-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                <div className="text-[10px] text-emerald-300 font-bold mb-1">Last event</div>
                <div className="text-[10px] font-mono text-emerald-200">
                  {diag.lastEvent.name} · {diag.lastEvent.at}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-white/40 py-8 text-center">Loading…</div>
        )}

        {/* Action row */}
        <div className="grid grid-cols-2 gap-2 mt-5">
          <button
            onClick={fireTest}
            disabled={firing || !diag?.enabled}
            className="flex items-center justify-center gap-2 py-3 rounded-2xl bg-brand-500 text-white font-bold text-xs active:scale-[0.98] disabled:opacity-40 disabled:active:scale-100 transition-transform"
            data-testid="appsflyer-debug-fire-btn"
          >
            <Zap className="w-4 h-4" />
            {firing ? "Firing…" : "Fire test event"}
          </button>
          <button
            onClick={copyAll}
            className="flex items-center justify-center gap-2 py-3 rounded-2xl bg-white/10 text-white font-bold text-xs active:bg-white/20"
            data-testid="appsflyer-debug-copy-btn"
          >
            <Copy className="w-4 h-4" />
            Copy all
          </button>
        </div>

        {!diag?.initialized && diag?.enabled && (
          <button
            onClick={forceInit}
            className="mt-2 w-full py-3 rounded-2xl bg-amber-500/20 text-amber-300 font-bold text-xs border border-amber-500/30 active:bg-amber-500/30"
            data-testid="appsflyer-debug-init-btn"
          >
            Force initSDK (not initialized yet)
          </button>
        )}

        <div className="mt-4 text-[10px] text-white/40 leading-relaxed">
          Expected: <span className="text-emerald-400">plugin ✓, enabled ✓, initialized ✓</span>. If any
          are red, that's the smoking gun. After firing a test event, wait 1-3 min then check
          AppsFlyer → Overview → Real-time.
        </div>
      </div>
    </div>
  );
}
